import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import pages from 'menu-items/pages';
import { NavItemType } from 'types/menu';

// Aplana el árbol de menú y extrae solo los ítems con URL
function flattenMenu(items: NavItemType[]): { label: string; url: string; icon: string }[] {
  const result: { label: string; url: string; icon: string }[] = [];
  for (const item of items) {
    if (item.type === 'item' && item.url) {
      result.push({ label: item.title as string, url: item.url, icon: (item.icon as string) ?? 'ph ph-circle' });
    }
    if (item.children) {
      result.push(...flattenMenu(item.children as NavItemType[]));
    }
  }
  return result;
}

// ==============================|| GLOBAL SEARCH ||============================== //

export default function GlobalSearch() {
  const [open, setOpen]   = useState(false);
  const [query, setQuery] = useState('');
  const wrapperRef        = useRef<HTMLDivElement>(null);
  const inputRef          = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Lista plana de ítems del menú (los títulos ya son strings en español)
  const menuItems = useMemo(() => {
    const allChildren = (pages.children ?? []) as NavItemType[];
    return flattenMenu(allChildren);
  }, []);

  // Filtrar por query
  const results = useMemo(() => {
    if (query.length < 2) return [];
    const q = query.toLowerCase();
    return menuItems.filter(item => item.label.toLowerCase().includes(q));
  }, [query, menuItems]);

  // Cerrar al hacer click fuera
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Cerrar con Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') { setOpen(false); setQuery(''); } };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const handleToggle = () => {
    setOpen(prev => !prev);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleSelect = (url: string) => {
    navigate(url);
    setQuery('');
    setOpen(false);
  };

  return (
    <div ref={wrapperRef} className="pc-h-item dropdown" style={{ position: 'relative' }}>
      {/* Botón lupa */}
      <button
        className="pc-head-link arrow-none m-0 trig-drp-search btn btn-link"
        onClick={handleToggle}
        aria-label="Buscar"
      >
        <i className="ph ph-magnifying-glass" />
      </button>

      {/* Panel de búsqueda */}
      {open && (
        <div
          className="pc-h-dropdown drp-search dropdown-menu show"
          style={{ minWidth: 280, maxHeight: 360, overflowY: 'auto' }}
        >
          {/* Input */}
          <div className="px-3 py-2 border-bottom">
            <div className="d-flex align-items-center gap-2">
              <i className="ph ph-magnifying-glass text-muted" />
              <input
                ref={inputRef}
                type="search"
                className="form-control border-0 shadow-none p-0"
                placeholder="Buscar en el menú..."
                value={query}
                onChange={e => setQuery(e.target.value)}
                autoComplete="off"
              />
            </div>
          </div>

          {/* Sin resultados */}
          {query.length >= 2 && results.length === 0 && (
            <div className="px-3 py-3 text-muted text-center small">
              Sin resultados para <strong>"{query}"</strong>
            </div>
          )}

          {/* Resultados */}
          {results.map((item, i) => (
            <button
              key={i}
              className="dropdown-item d-flex align-items-center gap-2 px-3 py-2"
              onClick={() => handleSelect(item.url)}
            >
              <i className={`${item.icon} text-primary fs-5`} />
              <span>{item.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
